import { Link } from 'react-router-dom'

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0a1929] via-[#0d2136] to-[#0f2744] text-white">
      {/* Logistics Network Background */}
      <div className="absolute inset-0 opacity-20">
        {/* World Map Dots Pattern */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1.5" fill="#D4AF37" opacity="0.4"/>
            </pattern>
            
            <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00B37E" stopOpacity="0"/>
              <stop offset="50%" stopColor="#00B37E" stopOpacity="0.6"/>
              <stop offset="100%" stopColor="#00B37E" stopOpacity="0"/>
            </linearGradient>
          </defs>
          
          {/* Dot Grid Background */}
          <rect width="100%" height="100%" fill="url(#dots)"/>
          
          {/* Shipping Routes - Curved Paths */}
          <path 
            d="M 100 400 Q 400 200, 800 300 T 1400 250" 
            fill="none" 
            stroke="url(#routeGradient)" 
            strokeWidth="2"
          />
          <path 
            d="M 200 500 Q 600 350, 1000 400 T 1600 380" 
            fill="none" 
            stroke="url(#routeGradient)" 
            strokeWidth="2"
          />
          <path 
            d="M 0 300 Q 300 450, 700 350 T 1200 320" 
            fill="none" 
            stroke="url(#routeGradient)" 
            strokeWidth="2"
          />
          
          {/* Hub Points */}
          <circle cx="100" cy="400" r="6" fill="#00B37E" opacity="0.8">
            <animate attributeName="r" values="6;8;6" dur="2s" repeatCount="indefinite"/>
          </circle>
          <circle cx="800" cy="300" r="6" fill="#00B37E" opacity="0.8">
            <animate attributeName="r" values="6;8;6" dur="2s" begin="0.5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="1400" cy="250" r="6" fill="#00B37E" opacity="0.8">
            <animate attributeName="r" values="6;8;6" dur="2s" begin="1s" repeatCount="indefinite"/>
          </circle>
          
          {/* Shipping Icons */}
          <g opacity="0.3">
            {/* Plane */}
            <path d="M 300 250 l 15 -5 l 3 -8 l -10 3 l -5 -12 l -3 0 l 2 12 l -10 3 l -2 -4 l -2 0 l 1 6 l 1 6 l 2 0 l 2 -4 l 10 3 l -2 12 l 3 0 l 5 -12 l 10 3 l -3 -8 z" fill="#D4AF37"/>
            
            {/* Container Ship */}
            <rect x="900" y="420" width="35" height="15" fill="#D4AF37"/>
            <rect x="905" y="410" width="8" height="10" fill="#D4AF37"/>
            <rect x="918" y="410" width="8" height="10" fill="#D4AF37"/>
            
            {/* Truck */}
            <rect x="1100" y="520" width="25" height="12" fill="#D4AF37"/>
            <rect x="1120" y="515" width="8" height="8" fill="#D4AF37"/>
            <circle cx="1108" cy="532" r="4" fill="#D4AF37"/>
            <circle cx="1125" cy="532" r="4" fill="#D4AF37"/>
          </g>
        </svg>
      </div>
      
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1929]/80 via-[#0a1929]/60 to-[#0a1929]/90" />

      <div className="section-container relative z-10 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Headline */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-6 animate-slide-up text-balance">
            Advanced Logistics Excellence Tailored to{' '}
            <span className="text-profit">Accelerate Your Growth</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl lg:text-2xl text-neutral-100 mb-8 max-w-3xl mx-auto animate-slide-up animation-delay-200">
            End-to-end freight forwarding, customs brokerage, and supply chain management services 
            connecting your business to global markets with speed, reliability, and expertise.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up animation-delay-400">
            <Link
              to="/contact"
              className="group relative px-8 py-4 bg-gradient-to-r from-primary to-primary-light text-white font-semibold rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto text-center"
            >
              <span className="relative z-10">Get In Touch</span>
              <div className="absolute inset-0 bg-gradient-to-r from-primary-light to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
            <Link
              to="/services"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#0a1929] w-full sm:w-auto text-center"
            >
              Our Services
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-3 gap-8 animate-fade-in animation-delay-600">
            <div className="backdrop-blur-sm bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="text-3xl md:text-4xl font-display font-bold text-profit mb-2">
                500+
              </div>
              <div className="text-sm text-neutral-200">Global Partners</div>
            </div>
            <div className="backdrop-blur-sm bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="text-3xl md:text-4xl font-display font-bold text-profit mb-2">
                95%
              </div>
              <div className="text-sm text-neutral-200">On-Time Delivery</div>
            </div>
            <div className="backdrop-blur-sm bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="text-3xl md:text-4xl font-display font-bold text-profit mb-2">
                24/7
              </div>
              <div className="text-sm text-neutral-200">Customer Support</div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave Separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
        >
          <path
            d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z"
            className="fill-neutral"
          />
        </svg>
      </div>
    </section>
  )
}

